import { useEffect, useState, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from "lucide-react"
import styles from './AddressOnline.module.scss';

interface Doctor {
  id: string;
  name: string;
  office_id: string[] | number[];
}

interface ScheduleRow {
  Time: string;
  OfficeId: string | number;
  DoctorId: string | number;
  DoctorFullName?: string;
  FacilityId?: string | number;
  Price?: number;
  Services?: any;
  MinimumPrepaymentAmount: null;
}

interface AddressProps {
  productId?: number;
  doctorId?: string;
  addressOption: any[];
  schedule: any[];
  selectedDate?: Date;
  doctors?: Doctor[];
  value?: { time: string; address: string | null; id: string | null; desc: string | null; doctorId?: string | null; doctorName?: string | null };
  onChange: (val:  { time: string; address: string | null; id: string | null; idsSameName: any; officeSameNameDoctors: any; desc: string | null; doctorId: string | null; doctorName: string | null   }) => void;
}

const formatTime24 = (s: string): string => {
  try {
    const t = s.includes('T') ? s.split('T')[1] : s;
    return t.slice(0, 5);
  } catch {
    const d = new Date(s);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
};

const AddressOnline = ({
  productId,
  doctorId,
  addressOption,
  schedule,
  selectedDate,
  doctors = [],
  value,
  onChange
}: AddressProps) => {
  const [availableAddress, setAvailableAddress] = useState<any[]>([]);
  const [availableTimeForDoctor, setAvailableDatesTimeForDoctor] = useState<Record<string, ScheduleRow[]>>({});
  const [timesSelectedDate, setTimesSelectedDate] = useState<ScheduleRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setForce] = useState(0);
  const forceUpdate = () => setForce(f => f + 1);

  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [scrollThumb, setScrollThumb] = useState<{ left: number; width: number }>({ left: 0, width: 100 });

  const updateScrollThumb = () => {
    const el = sliderRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      if (scrollWidth <= clientWidth) {
        setScrollThumb({ left: 0, width: 100 });
        el.style.setProperty('--scroll-width', '0%');
      } else {
        const width = (clientWidth / scrollWidth) * 100;
        const left = (scrollLeft / scrollWidth) * 100;
        setScrollThumb({ left, width });
        el.style.setProperty('--scroll-width', `${width}%`);
        el.style.setProperty('--scroll-left', `${left}%`);
      }
    }
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (el) {
      const handler = () => updateScrollThumb();
      el.addEventListener('scroll', handler);
      window.addEventListener('resize', handler);
      updateScrollThumb();
      return () => {
        el.removeEventListener('scroll', handler);
        window.removeEventListener('resize', handler);
      }
    }
  }, [availableTimeForDoctor]);

  useEffect(() => {
    setTimeout(() => {
      forceUpdate();
    }, 100);
  }, [selectedDate]);

  const handleScrollLeft = () => {
    const el = sliderRef.current;
    if (el) {
      el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
    }
  };
  const handleScrollRight = () => {
    const el = sliderRef.current;
    if (el) {
      el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
    }
  };

  const canScrollLeft = () => {
    const el = sliderRef.current;
    if (!el) return false;
    return el.scrollLeft > 0;
  };

  const canScrollRight = () => {
    const el = sliderRef.current;
    if (!el) return false;
    return el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  };

  useEffect(() => {
    try {
      setIsLoading(true);
      setError(null);
      setAvailableAddress(addressOption);
    } catch (err) {
      setError('Error setting addresses');
      console.error('Error setting addresses:', err);
    } finally {
      setIsLoading(false);
    }
  }, [addressOption]);

  useEffect(() => {
    try {
      if (selectedDate && schedule.length > 0) {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        const found = schedule.find((item: any) => {
          let scheduleDate = item.Date.includes('T') ? item.Date.split('T')[0] : item.Date;
          return scheduleDate === dateStr;
        });
        setTimesSelectedDate(found ? found.Rows : []);
      } else {
        setTimesSelectedDate([]);
      }
    } catch (err) {
      console.error('Error getting time for selected date:', err);
      setTimesSelectedDate([]);
    }
  }, [schedule, selectedDate]);

  useEffect(() => {
    try {
      if (timesSelectedDate.length === 0) {
        setAvailableDatesTimeForDoctor({});
        return;
      }

      let filteredTimes = timesSelectedDate;

      if (doctorId && doctorId !== '') {
        filteredTimes = filteredTimes.filter((row: ScheduleRow) => 
          String(row.DoctorId) === String(doctorId)
        );
      }

      const groupedByOffice = filteredTimes.reduce((acc: Record<string, ScheduleRow[]>, row: ScheduleRow) => {
        const officeId = String(row.OfficeId);
        if (!acc[officeId]) {
          acc[officeId] = [];
        }
        acc[officeId].push(row);
        return acc;
      }, {});

      setAvailableDatesTimeForDoctor(groupedByOffice);
    } catch (err) {
      console.error('Error filtering available times:', err);
      setAvailableDatesTimeForDoctor({});
    }
  }, [timesSelectedDate, doctorId]);

  const processedTimes = useMemo(() => {
    let allOfficeTimes: ScheduleRow[] = [];
    if (availableTimeForDoctor) {
        Object.values(availableTimeForDoctor).forEach(times => {
            allOfficeTimes.push(...times);
        });
    }

    const nowMs = Date.now();
    allOfficeTimes = allOfficeTimes.filter(row => new Date(row.Time).getTime() >= nowMs);

    let result = allOfficeTimes;
    if (!doctorId) {
        const uniqueTimes = new Map<string, ScheduleRow>();
        allOfficeTimes.forEach(row => {
            const time = formatTime24(row.Time);
            if (!uniqueTimes.has(time)) {
                uniqueTimes.set(time, row);
            }
        });
        result = Array.from(uniqueTimes.values());
    }

    result.sort((a, b) => new Date(a.Time).getTime() - new Date(b.Time).getTime());
    return result;
  }, [availableTimeForDoctor, doctorId]);

  useEffect(() => {
    if (value && value.time) return;
    if (processedTimes.length === 0) return;

    const firstRow = processedTimes[0];
    const time = formatTime24(firstRow.Time);
    
    const slotOfficeId = String(firstRow.OfficeId);
    const slotOffice = availableAddress.find(addr => String(addr.id) === slotOfficeId);
    const officeName = slotOffice ? slotOffice.address : '';
    const officeDesc = slotOffice ? slotOffice.desk : '';

    const officesWithSameName = availableAddress.filter(addr => addr.address === officeName);
    const sameNameOfficeIds = officesWithSameName.map(o => String(o.id));
    const orderedIdsForSameName = [slotOfficeId, ...sameNameOfficeIds.filter(id => id !== slotOfficeId)].join(', ');

    const allTimes: ScheduleRow[] = [];
    Object.values(availableTimeForDoctor).forEach(times => allTimes.push(...times));
    const uniqueDoctorsMap = new Map<string, { id: string; name: string }>();
    allTimes.forEach(r => {
        const dId = String(r.DoctorId);
        if (!uniqueDoctorsMap.has(dId)) {
            uniqueDoctorsMap.set(dId, { id: dId, name: r.DoctorFullName ?? '' });
        }
    });
    const doctorsInSameName = Array.from(uniqueDoctorsMap.values());
    const doctorsSameNameAttr = JSON.stringify(doctorsInSameName);

    onChange({
        time: time,
        id: slotOfficeId,
        idsSameName: orderedIdsForSameName,
        officeSameNameDoctors: doctorsSameNameAttr,
        address: officeName,
        desc: officeDesc,
        doctorId: String(firstRow.DoctorId),
        doctorName: firstRow.DoctorFullName || ''
    });

  }, [processedTimes, value, availableAddress, availableTimeForDoctor, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedTime = e.target.value;
      const officeId = e.target.getAttribute('data-r-office-id-slot');
      const officeName = e.target.getAttribute('data-r-office-name');
      const officeDesc = e.target.getAttribute('data-r-office-desc');
      const doctorId = e.target.getAttribute('data-r-doctor-id-slot');
      const doctorName = e.target.getAttribute('data-r-doctor-name-slot');

      const officesWithSameName = availableAddress.filter(addr => addr.address === officeName);
      const sameNameOfficeIds = officesWithSameName.map(o => String(o.id));
      const orderedIdsForSameName = [officeId, ...sameNameOfficeIds.filter(id => id !== officeId)].join(', ');

      const allOfficeTimes: ScheduleRow[] = [];
      Object.values(availableTimeForDoctor).forEach(times => allOfficeTimes.push(...times));
      const uniqueDoctorsMap = new Map<string, { id: string; name: string }>();
      allOfficeTimes.forEach(r => {
        const dId = String(r.DoctorId);
        if (!uniqueDoctorsMap.has(dId)) {
          uniqueDoctorsMap.set(dId, { id: dId, name: r.DoctorFullName ?? '' });
        }
      });
      const doctorsInSameName = Array.from(uniqueDoctorsMap.values());
      const doctorsSameNameAttr = JSON.stringify(doctorsInSameName);

      onChange({
        time: String(selectedTime),
        id: officeId,
        idsSameName: orderedIdsForSameName,
        officeSameNameDoctors: doctorsSameNameAttr,
        address: officeName,
        desc: officeDesc,
        doctorId: doctorId,
        doctorName: doctorName
      });
    } catch (err) {
      console.error('Error handling time selection:', err);
    }
  };

  if (isLoading) {
    return (
      <div className={styles['loading-container']}>
        <div className={styles['loading-spinner']}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['error-container']}>
        <p className={styles['error-message']}>{error}</p>
        <button 
          type="button"
          className={styles['retry-button']}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.reload();
          }}
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className={styles['address-container']}>
      {processedTimes.length === 0 ? (
        <div className={styles['no-offices']}>
          <p>Brak dostępnych terminów.</p>
        </div>
      ) : (
        <div className={styles['office-container']}>
          <div className={styles['schedule-container']}>
            <div className={styles['prev-slot']}>
              <button
                type="button"
                className={styles['slider-arrow']}
                onClick={handleScrollLeft}
                aria-label="Попередні слоти"
                disabled={!canScrollLeft()}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            </div>
            <div
              className={styles['time-slots-container'] + ' ' + styles['always-scrollbar']}
              ref={sliderRef}
            >
              {processedTimes.map((row: ScheduleRow, timeIndex: number) => {
                const time = formatTime24(row.Time);
                
                const slotOfficeId = String(row.OfficeId);
                const slotOffice = availableAddress.find(addr => String(addr.id) === slotOfficeId);
                const officeName = slotOffice ? slotOffice.address : '';
                const officeDesc = slotOffice ? slotOffice.desk : '';
                  
                return (
                  <div
                    key={`${time}-${slotOfficeId}-${timeIndex}`}
                    className={styles['time-slot-wrapper']}
                  >
                    <input
                      type='radio'
                      id={`slot-r-${time}-${slotOfficeId}-${timeIndex}`}
                      name='address-slot'
                      value={time}
                      data-r-doctor-id-slot={row.DoctorId}
                      data-r-doctor-name-slot={row.DoctorFullName}
                      data-r-office-id-slot={slotOfficeId}
                      data-r-office-name={officeName}
                      data-r-office-desc={officeDesc}
                      style={{display: 'none'}}
                      checked={value?.time === time && String(value?.id) === String(slotOfficeId)}
                      onChange={handleChange}
                      aria-label={`Вибір часу ${time} для кабінету ${officeName}`}
                    />
                    <label
                      htmlFor={`slot-r-${time}-${slotOfficeId}-${timeIndex}`}
                      className={styles['time-slot-r-item']}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          const input = document.getElementById(
                            `slot-r-${time}-${slotOfficeId}-${timeIndex}`
                          ) as HTMLInputElement;
                          if (input) {
                            input.checked = true;
                            input.dispatchEvent(
                              new Event('change', {bubbles: true})
                            );
                          }
                        }
                      }}
                    >
                      <span className={styles['time-text']}>{time}</span>
                    </label>
                  </div>
                );
              })}
            </div>
            <div className={styles['custom-scrollbar-container']}>
              <div className={styles['custom-scrollbar-track']} />
              {(!canScrollLeft() && !canScrollRight() ? '' : 
              <div 
                className={styles['custom-scrollbar-thumb']}
                style={{
                  width: `${scrollThumb.width || 100}%`,
                  left: `${scrollThumb.left || 0}%`,
                }}
              />
            )}
            </div>
            <div className={styles['next-slot']}>
              <button
                type="button"
                className={styles['slider-arrow']}
                onClick={handleScrollRight}
                aria-label="Наступні слоти"
                disabled={!canScrollRight()}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressOnline;
